import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Grid, CircularProgress, Alert } from '@mui/material';
import { tenantApiFetch } from './tenantApi';
import { useTenantAuth } from '../context/TenantAuthContext';
import { TENANT_MAIN_DASHBOARD_TYPE } from '../dashboard/widgetCatalog';

function spanProps(config) {
  const s = (config && config.span) || {};
  return {
    xs: s.xs != null ? s.xs : 12,
    sm: s.sm != null ? s.sm : 6,
    md: s.md != null ? s.md : 4
  };
}

function renderTenantWidget(w, data, tenant) {
  const sp = spanProps(w.config_json);
  const key = w.widget_code;
  switch (w.widget_code) {
    case 'stat_active_users':
      return (
        <Grid item xs={sp.xs} sm={sp.sm} md={sp.md} key={key}>
          <Paper sx={{ p: 2 }}>
            <Typography color="text.secondary">{w.widget_name}</Typography>
            <Typography variant="h5">{data?.counts?.activeUsers ?? '—'}</Typography>
          </Paper>
        </Grid>
      );
    case 'stat_roles':
      return (
        <Grid item xs={sp.xs} sm={sp.sm} md={sp.md} key={key}>
          <Paper sx={{ p: 2 }}>
            <Typography color="text.secondary">{w.widget_name}</Typography>
            <Typography variant="h5">{data?.counts?.roles ?? '—'}</Typography>
          </Paper>
        </Grid>
      );
    case 'stat_tenant_status':
      return (
        <Grid item xs={sp.xs} sm={sp.sm} md={sp.md} key={key}>
          <Paper sx={{ p: 2 }}>
            <Typography color="text.secondary">{w.widget_name}</Typography>
            <Typography variant="h5" sx={{ textTransform: 'capitalize' }}>
              {data?.tenant?.status ?? tenant?.status ?? '—'}
            </Typography>
          </Paper>
        </Grid>
      );
    default:
      return (
        <Grid item xs={sp.xs} sm={sp.sm} md={sp.md} key={key}>
          <Paper sx={{ p: 2 }}>
            <Typography color="text.secondary">{w.widget_name}</Typography>
            <Typography variant="body2" color="text.disabled">
              Unknown widget code <code>{w.widget_code}</code>
            </Typography>
          </Paper>
        </Grid>
      );
  }
}

function LegacyDashboardGrid({ data, tenant }) {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={4}>
        <Paper sx={{ p: 2 }}>
          <Typography color="text.secondary">Active users</Typography>
          <Typography variant="h5">{data?.counts?.activeUsers ?? '—'}</Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <Paper sx={{ p: 2 }}>
          <Typography color="text.secondary">Roles</Typography>
          <Typography variant="h5">{data?.counts?.roles ?? '—'}</Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <Paper sx={{ p: 2 }}>
          <Typography color="text.secondary">Tenant status</Typography>
          <Typography variant="h5" sx={{ textTransform: 'capitalize' }}>
            {data?.tenant?.status ?? tenant?.status ?? '—'}
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  );
}

export default function TenantDashboard() {
  const { tenant } = useTenantAuth();
  const [data, setData] = useState(null);
  const [layout, setLayout] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [sumRes, layRes] = await Promise.all([
          tenantApiFetch('/api/tenant/dashboard/summary'),
          tenantApiFetch(
            `/api/tenant/dashboard/layout?dashboard_type=${encodeURIComponent(TENANT_MAIN_DASHBOARD_TYPE)}`
          )
        ]);
        const sumJson = await sumRes.json().catch(() => ({}));
        const layJson = await layRes.json().catch(() => ({}));
        if (!sumRes.ok) throw new Error(sumJson.error || 'Failed to load dashboard');
        if (!cancelled) {
          setData(sumJson);
          if (layRes.ok && layJson && !layJson.useLegacyDefaults && layJson.widgets?.length) {
            setLayout(layJson);
          } else {
            setLayout(null);
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Tenant: <strong>{tenant?.name}</strong> — Proctoring live console remains at{' '}
        <code>/admin</code> (separate from this RBAC shell).
      </Typography>
      {layout && layout.widgets && layout.widgets.length > 0 ? (
        <Grid container spacing={2}>
          {layout.widgets.map((w) => renderTenantWidget(w, data, tenant))}
        </Grid>
      ) : (
        <LegacyDashboardGrid data={data} tenant={tenant} />
      )}
    </Box>
  );
}
